// src/pages/CareerDetail.jsx
import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Toast from '../components/Toast';
import { FALLBACK_JOBS } from '../data/careersData';

function LocationIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

function InlineApplyForm({ job }) {
  const [form, setForm] = useState({ name: '', email: '', resumeLink: '' });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, 'job_applications'), {
        name: form.name,
        email: form.email,
        resumeUrl: form.resumeLink,
        jobId: job.id,
        jobTitle: job.title,
        submittedAt: serverTimestamp(),
        source: 'website',
        status: 'new',
      });
      setSubmitted(true);
    } catch (err) {
      setToast({ message: 'Submission failed. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
        <p style={{ fontSize: '32px', marginBottom: '12px' }}>✓</p>
        <h3 className="title-6" style={{ marginBottom: '8px' }}>Application submitted!</h3>
        <p className="body-sm" style={{ color: 'var(--token-desc)' }}>We'll review it and reach out soon.</p>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--shades-200)', borderRadius: '12px', padding: '32px' }}>
      <h3 className="title-5" style={{ marginBottom: '4px' }}>{job.title}</h3>
      <p className="body-sm" style={{ color: 'var(--token-desc)', marginBottom: '24px' }}>{job.type || job.period} · {job.level}</p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div className="form-group">
          <label className="form-label" htmlFor="cd-name">Name <span className="required">*</span></label>
          <input id="cd-name" name="name" type="text" className="form-input" placeholder="Full name" value={form.name} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cd-email">Email <span className="required">*</span></label>
          <input id="cd-email" name="email" type="email" className="form-input" placeholder="Email address" value={form.email} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cd-resume">CV Link</label>
          <input id="cd-resume" name="resumeLink" type="url" className="form-input" placeholder="Google Drive, Dropbox link..." value={form.resumeLink} onChange={handleChange} />
          <p className="body-sm" style={{ color: 'var(--token-desc)', marginTop: '4px' }}>Paste a shareable link to your CV</p>
        </div>
        <button type="submit" className="btn btn-primary btn-full" disabled={loading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
          {loading && <span className="spinner" />}
          {loading ? 'Submitting...' : 'Apply now'}
        </button>
      </form>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default function CareerDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchJob() {
      try {
        const docRef = doc(db, 'jobs', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setJob({ id: snap.id, ...snap.data() });
        } else {
          const found = FALLBACK_JOBS.find(j => j.id === id);
          if (found) setJob(found);
          else setNotFound(true);
        }
      } catch {
        const found = FALLBACK_JOBS.find(j => j.id === id);
        if (found) setJob(found);
        else setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    fetchJob();
  }, [id]);

  if (notFound) return <Navigate to="/careers" replace />;

  const requirements = job?.requirements
    ? job.requirements.split('\n').filter(r => r.trim())
    : [];

  const tags = job
    ? (typeof job.tags === 'string' ? job.tags.split(',') : (job.tags || [])).map(t => t.trim()).filter(Boolean)
    : [];

  return (
    <>
      {job && (
        <SEO
          title={`${job.title} — Careers`}
          description={job.desc || job.description || `${job.title} at District Sourcing Tech`}
          url={`https://districtsourcingtech.com/careers/${id}`}
        />
      )}
      <Navbar variant="light" />

      {loading ? (
        <section style={{ padding: '128px 32px', textAlign: 'center', minHeight: '60vh' }}>
          <span className="spinner" style={{ borderColor: 'rgba(0,0,0,0.1)', borderTopColor: 'var(--primary-500)', margin: '0 auto', display: 'block' }} />
        </section>
      ) : job && (
        <section style={{ padding: '64px 32px', maxWidth: '1376px', margin: '0 auto' }}>
          {/* Back link */}
          <Link to="/careers" style={{ color: 'var(--primary-500)', fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '32px', textDecoration: 'none' }}>
            ← Back to Careers
          </Link>

          <div className="career-detail-layout">
            {/* Left: Job details */}
            <div className="career-detail-left">
              <h1 className="title-2" style={{ marginBottom: '12px' }}>{job.title} ({job.type || job.period})</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--token-desc)', marginBottom: '32px' }}>
                <LocationIcon />
                <span className="body-lg">Remote / Hybrid</span>
              </div>

              {/* Key-value pairs */}
              <div className="career-detail-meta">
                <div className="career-detail-meta-row">
                  <span className="career-detail-meta-label">Job type :</span>
                  <span className="career-detail-meta-value">{(job.type || job.period || 'Contract based')}</span>
                </div>
                <div className="career-detail-meta-row">
                  <span className="career-detail-meta-label">Job level :</span>
                  <span className="career-detail-meta-value">{job.level || 'Senior level'}</span>
                </div>
                <div className="career-detail-meta-row">
                  <span className="career-detail-meta-label">Salary :</span>
                  <span className="career-detail-meta-value">Negotiable</span>
                </div>
                {job.deadline && (
                  <div className="career-detail-meta-row">
                    <span className="career-detail-meta-label">Deadline :</span>
                    <span className="career-detail-meta-value">{job.deadline}</span>
                  </div>
                )}
              </div>

              {/* Role overview */}
              <div style={{ marginBottom: '40px' }}>
                <h2 className="title-4" style={{ marginBottom: '16px' }}>Role overview</h2>
                <p className="body-lg" style={{ color: 'var(--token-desc)', lineHeight: 1.75 }}>
                  {job.description || job.desc}
                </p>
              </div>

              {/* Responsibilities / Requirements */}
              {requirements.length > 0 && (
                <div style={{ marginBottom: '40px' }}>
                  <h2 className="title-4" style={{ marginBottom: '16px' }}>Responsibilities</h2>
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: '12px', listStyle: 'none' }}>
                    {requirements.slice(0, Math.ceil(requirements.length / 2)).map((req, i) => (
                      <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <span style={{ color: 'var(--primary-500)', marginTop: '4px', flexShrink: 0 }}>•</span>
                        <span className="body-lg" style={{ color: 'var(--token-desc)' }}>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {requirements.length > 1 && (
                <div style={{ marginBottom: '40px' }}>
                  <h2 className="title-4" style={{ marginBottom: '16px' }}>Requirements</h2>
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: '12px', listStyle: 'none' }}>
                    {requirements.slice(Math.ceil(requirements.length / 2)).map((req, i) => (
                      <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <span style={{ color: 'var(--primary-500)', marginTop: '4px', flexShrink: 0 }}>•</span>
                        <span className="body-lg" style={{ color: 'var(--token-desc)' }}>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tags.length > 0 && (
                <div className="service-tags">
                  {tags.map(t => <span key={t} className="service-tag">{t}</span>)}
                </div>
              )}
            </div>

            {/* Right: Inline Apply form */}
            <div className="career-detail-right">
              <InlineApplyForm job={job} />
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}